import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import {
  ArrowRight,
  BarChart3,
  Building2,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronDown,
  FileText,
  Mail,
  Loader2,
  MapPin,
  Menu,
  Quote,
  Send,
  ShieldCheck,
  Sparkles,
  Star,
  Users,
  X,
} from "lucide-react";
import api from "../lib/api";

/* ---------------------------------- data --------------------------------- */

const navLinks = [
  { label: "Why Hoardexa", id: "why-hoardexa" },
  { label: "Simple Flow", id: "operations" },
  { label: "Pricing", id: "pricing" },
  { label: "FAQ", id: "faq" },
];

const contactEmails = [
  { label: "Sales", email: "sales@hoardexa.com", note: "Demos, pricing, and plan advice" },
  { label: "General", email: "contact@hoardexa.com", note: "Partnerships and other enquiries" },
];

const whyHoardexa = [
  {
    icon: Building2,
    title: "Single platform for all your outdoor advertising work",
    text: "Manage hoardings, clients, proposals, campaigns, billing, payments, reports, and business analysis from one place.",
    featured: true,
  },
  {
    icon: Sparkles,
    title: "Custom PPT proposal automation",
    text: "Upload your own PPT format and let Hoardexa fill it with selected hoarding photos, locations, sizes, rates, and client details. No need to redesign your proposal style.",
  },
  {
    icon: CalendarDays,
    title: "Proposal to campaign conversion",
    text: "Once the client approves, create the campaign from the proposal itself. Selected sites, dates, client details, and amounts carry forward.",
  },
  {
    icon: FileText,
    title: "Inventory and availability control",
    text: "Manage hoarding photos, locations, sizes, rent, selling rate, city, status, and availability so your team always works from updated inventory.",
  },
  {
    icon: CheckCircle2,
    title: "Finance, billing, and Tally-ready exports",
    text: "Manage invoices, payments, GST summary, ledgers, reconciliation, and Tally-ready exports from the same campaign and accounting records.",
  },
  {
    icon: Users,
    title: "Employee creation with micro access control",
    text: "Create employees and give exact permissions: who can view hoardings, create campaigns, edit rates, see client contacts, view payments, export reports, manage invoices, or access accounts.",
  },
  {
    icon: BarChart3,
    title: "AI business analysis",
    text: "See which hoardings are earning, which are idle, which cities are performing, where revenue is pending, and what needs your attention next.",
  },
];

const steps = [
  {
    num: "01",
    icon: Users,
    title: "Add client and inquiry",
    text: "Enter the client name, brand, contact person, city, dates, and campaign requirement.",
  },
  {
    num: "02",
    icon: Building2,
    title: "Pick matching hoardings",
    text: "Choose sites from saved inventory with photos, rates, size, location, and availability already visible.",
  },
  {
    num: "03",
    icon: FileText,
    title: "Generate the proposal PPT",
    text: "Create a polished PowerPoint in your own format, ready to send with selected hoardings already filled in.",
  },
  {
    num: "04",
    icon: CalendarDays,
    title: "Create campaign from approval",
    text: "When the client approves, convert the proposal into a campaign without entering the same details again.",
  },
  {
    num: "05",
    icon: CheckCircle2,
    title: "Send accounts-ready data",
    text: "Prepare invoices, payments, GST, and Tally-ready data from the campaign itself.",
  },
  {
    num: "06",
    icon: BarChart3,
    title: "Understand what to focus on",
    text: "See which hoardings earn well, which stay idle, where payments are pending, and what needs attention next.",
  },
];

const plans = [
  {
    name: "Standard",
    tagline: "For owners who want clean inventory and faster proposals.",
    features: [
      "Client and hoarding records",
      "Ready proposal format",
      "Simple campaign tracking",
      "Email support",
      "Basic business dashboard",
      "Payment status overview",
    ],
    highlighted: false,
  },
  {
    name: "Pro",
    tagline: "For growing teams that need sales and collection control.",
    features: [
      "Team access for daily work",
      "PPT and PDF proposal generation",
      "Employee creation and assignment",
      "Priority support",
      "City and campaign reports",
      "Client receivables follow-up",
    ],
    highlighted: false,
  },
  {
    name: "Premium",
    tagline: "For operators who want the full proposal-to-accounts flow.",
    features: [
      "Multiple team and office access",
      "Custom PPT templates and share links",
      "Employee permissions",
      "Priority support with onboarding",
      "Pricing and business analysis",
      "Rent, expenses, P&L, work queue",
    ],
    highlighted: true,
  },
  {
    name: "Enterprise",
    tagline: "For businesses that need custom setup and accounting depth.",
    features: [
      "Assisted setup for multiple offices",
      "Custom proposal templates and workflow setup",
      "Custom roles, permissions, training",
      "Dedicated account manager and SLA",
      "Custom reports and leadership analysis",
      "Invoices, GST, Tally export, reconciliation",
    ],
    highlighted: false,
  },
];

const testimonials = [
  {
    quote:
      "Before Hoardexa, availability lived in three spreadsheets and one person's memory. Now anyone on the team can answer a client with photos, rates, and dates in seconds.",
    name: "Rajesh Patil",
    role: "Owner, Skyline Outdoor Media",
    city: "Kolhapur",
  },
  {
    quote:
      "The receivables view alone paid for itself. We recovered pending payments we had lost track of, and month-end reporting went from two days to twenty minutes.",
    name: "Sneha Kulkarni",
    role: "Operations Head, MetroSign Ads",
    city: "Pune",
  },
  {
    quote:
      "Proposals used to take half a day of copy-pasting photos and rates. Now we filter available sites, export, and send. Clients notice the difference in how fast we respond.",
    name: "Amit Deshmukh",
    role: "Director, Vistara Publicity",
    city: "Sangli",
  },
];

const faqs = [
  {
    q: "Do I need to understand every feature before starting?",
    a: "No. Most teams begin with three simple actions: add clients, add hoardings, and create proposals. The deeper campaign, payment, and accounts tools become useful as your team starts working inside Hoardexa.",
  },
  {
    q: "Can I use my own PowerPoint proposal format?",
    a: "Yes. In higher plans, your preferred PPT design can be set up as a template. Hoardexa then fills it with selected hoarding photos, rates, location details, and client information.",
  },
  {
    q: "Can we import existing Excel data?",
    a: "Yes. Your existing hoarding, client, campaign, payment, rent, and finance records can be imported through structured templates so you do not have to enter everything manually.",
  },
  {
    q: "What happens after a proposal is approved?",
    a: "You can create the campaign from the proposal itself. That keeps the selected sites, dates, client, amount, and billing details connected from sales to operations.",
  },
  {
    q: "Can my accounts team use the data?",
    a: "Yes. Hoardexa keeps campaign, invoice, payment, GST, and ledger information organized so the team can download Tally-ready data when needed.",
  },
  {
    q: "Can employees see only what they are allowed to see?",
    a: "Yes. Access can be limited by role, work area, proposal permissions, reports, finance, and inventory visibility so sensitive information stays protected.",
  },
];

/* -------------------------------- helpers -------------------------------- */

function scrollTo(id) {
  const target = document.getElementById(id);
  if (!target) return;

  const navbarOffset = 84;
  const top = target.getBoundingClientRect().top + window.scrollY - navbarOffset;
  window.scrollTo({ top, behavior: "smooth" });
}

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.5, ease: "easeOut" },
};

function SectionHeader({ eyebrow, title, text, center = false }) {
  return (
    <motion.div {...reveal} className={`max-w-2xl ${center ? "mx-auto text-center" : ""}`}>
      <div className={`flex items-center gap-3 ${center ? "justify-center" : ""}`}>
        <span className="h-px w-8 bg-blue-600" />
        <span className="text-[11px] font-black uppercase tracking-[0.24em] text-blue-600">{eyebrow}</span>
        {center && <span className="h-px w-8 bg-blue-600" />}
      </div>
      <h2 className="mt-4 font-display text-3xl font-black tracking-tight text-slate-950 sm:text-4xl">{title}</h2>
      {text && <p className="mt-4 text-sm font-semibold leading-relaxed text-slate-600 sm:text-base">{text}</p>}
    </motion.div>
  );
}

function BrandMark({ light = false }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white shadow-lg shadow-blue-700/25">
        <span className="font-display text-lg font-black leading-none">H</span>
      </div>
      <div>
        <div className={`font-display text-lg font-black leading-none ${light ? "text-white" : "text-slate-950"}`}>
          Hoardexa
        </div>
        <div
          className={`mt-1 text-[10px] font-black uppercase tracking-[0.28em] ${
            light ? "text-slate-400" : "text-slate-500"
          }`}
        >
          Outdoor Ads
        </div>
      </div>
    </div>
  );
}

/* --------------------------------- navbar -------------------------------- */

function Navbar() {
  const [open, setOpen] = useState(false);
  const go = (id) => {
    setOpen(false);
    scrollTo(id);
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-slate-200/80 bg-white/90 backdrop-blur-xl">
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <button type="button" onClick={() => go("top")} className="text-left">
          <BrandMark />
        </button>

        <nav className="hidden items-center gap-1 lg:flex">
          {navLinks.map((link) => (
            <button
              key={link.id}
              type="button"
              onClick={() => go(link.id)}
              className="rounded-lg px-3.5 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-100 hover:text-slate-950"
            >
              {link.label}
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <Link
            to="/careers"
            className="rounded-lg px-4 py-2 text-sm font-bold text-slate-900 transition hover:bg-slate-100"
          >
            Careers
          </Link>
          <Link
            to="/login"
            className="rounded-lg px-4 py-2 text-sm font-bold text-slate-900 transition hover:bg-slate-100"
          >
            Sign In
          </Link>
          <button
            type="button"
            onClick={() => go("contact")}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-black text-white shadow-lg shadow-blue-700/25 transition hover:bg-blue-700"
          >
            Request Demo <ArrowRight size={15} />
          </button>
        </div>

        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="rounded-xl border border-slate-200 p-2 text-slate-900 lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="border-t border-slate-200 bg-white px-4 py-3 lg:hidden"
          >
            <div className="space-y-1">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  type="button"
                  onClick={() => go(link.id)}
                  className="block w-full rounded-xl px-3 py-2.5 text-left text-sm font-bold text-slate-900 hover:bg-slate-100"
                >
                  {link.label}
                </button>
              ))}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Link
                to="/careers"
                className="rounded-xl border border-slate-200 px-3 py-2.5 text-center text-sm font-black text-slate-900"
              >
                Careers
              </Link>
              <Link
                to="/login"
                className="rounded-xl border border-slate-200 px-3 py-2.5 text-center text-sm font-black text-slate-900"
              >
                Sign In
              </Link>
              <button
                type="button"
                onClick={() => go("contact")}
                className="rounded-xl bg-blue-600 px-3 py-2.5 text-sm font-black text-white"
              >
                Request Demo
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

/* ----------------------------- product mockup ---------------------------- */

function ProductPanel() {
  const cities = [
    ["Kolhapur", "KOLH-001", "Occupied", "bg-emerald-50 text-emerald-700 border-emerald-200", "w-[78%] bg-emerald-500"],
    ["Pune", "PUN-014", "Available", "bg-blue-50 text-blue-700 border-blue-200", "w-[42%] bg-blue-500"],
    ["Sangli", "SAN-008", "Maint.", "bg-amber-50 text-amber-700 border-amber-200", "w-[28%] bg-amber-500"],
  ];

  return (
    <div className="relative">
      {/* soft glow behind the panel */}
      <div className="absolute -inset-6 rounded-[32px] bg-gradient-to-br from-blue-100 via-white to-slate-100 blur-2xl" />

      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-950/10">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-blue-600">Proposal Workflow</div>
            <div className="font-display text-lg font-black text-slate-950">Client proposal ready</div>
          </div>
          <div className="flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[10px] font-black text-emerald-700">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" /> Ready
          </div>
        </div>

        <div className="grid grid-cols-3 border-b border-slate-200">
          {[
            ["12", "Sites selected", "text-blue-700"],
            ["4", "Cities covered", "text-emerald-700"],
            ["PPT", "Custom format", "text-amber-600"],
          ].map(([value, label, color]) => (
            <div key={label} className="border-r border-slate-200 p-4 last:border-r-0">
              <div className={`font-display text-xl font-black sm:text-2xl ${color}`}>{value}</div>
              <div className="mt-1 text-[10px] font-black uppercase tracking-[0.14em] text-slate-500">{label}</div>
            </div>
          ))}
        </div>

        <div className="p-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="font-display text-sm font-black text-slate-950">Selected hoardings</div>
            <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Client draft</div>
          </div>
          <div className="space-y-2">
            {cities.map(([city, id, state, badge, bar]) => (
              <div
                key={id}
                className="grid grid-cols-[1fr_auto] items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/70 p-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-display text-sm font-black text-slate-950">{city}</span>
                    <span className="truncate font-mono text-[11px] font-bold text-slate-500">{id}</span>
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-slate-200">
                    <div className={`h-2 rounded-full ${bar}`} />
                  </div>
                </div>
                <span className={`rounded-lg border px-2.5 py-1 text-[10px] font-black ${badge}`}>{state}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* floating alert card */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.5 }}
        className="absolute -bottom-5 -left-3 hidden items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-xl shadow-slate-950/10 sm:flex"
      >
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
          <FileText size={16} />
        </div>
        <div>
          <div className="text-xs font-black text-slate-950">Exported in your PPT style</div>
          <div className="text-[11px] font-bold text-slate-500">Photos, rates and locations filled</div>
        </div>
      </motion.div>
    </div>
  );
}

/* ---------------------------------- hero --------------------------------- */

function Hero() {
  return (
    <section id="top" className="relative overflow-hidden bg-white pt-[72px] scroll-mt-24">
      {/* subtle background texture */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 right-[-10%] h-[480px] w-[480px] rounded-full bg-blue-100/60 blur-3xl" />
        <div className="absolute bottom-[-20%] left-[-8%] h-[380px] w-[380px] rounded-full bg-slate-100 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgb(226 232 240 / 0.5) 1px, transparent 1px), linear-gradient(to bottom, rgb(226 232 240 / 0.5) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
            maskImage: "radial-gradient(ellipse 80% 60% at 50% 0%, black, transparent)",
            WebkitMaskImage: "radial-gradient(ellipse 80% 60% at 50% 0%, black, transparent)",
          }}
        />
      </div>

      <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 pb-8 pt-4 sm:px-6 lg:grid-cols-[1fr_0.88fr] lg:gap-12 lg:px-8 lg:pb-12 lg:pt-7">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="max-w-2xl"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3.5 py-1.5 text-[11px] font-black uppercase tracking-[0.18em] text-blue-700">
            <Sparkles size={13} /> Built for hoarding owners and outdoor ad teams
          </div>

          <h1 className="mt-5 font-display text-4xl font-black leading-[1.04] tracking-tight text-slate-950 sm:text-5xl lg:text-[56px]">
            From client inquiry to{" "}
            <span className="relative whitespace-nowrap text-blue-600">
              ready proposal
              <svg
                className="absolute -bottom-1.5 left-0 w-full text-blue-200"
                viewBox="0 0 200 9"
                fill="none"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <path d="M2 7C50 2 150 2 198 7" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
              </svg>
            </span>
            , without repeated work.
          </h1>

          <p className="mt-5 max-w-xl text-base font-semibold leading-relaxed text-slate-600 sm:text-lg">
            Add your clients, save your hoarding inventory, select the right sites, create a PowerPoint proposal in
            your own format, convert approval into a campaign, and send accounts clean Tally-ready data.
          </p>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => scrollTo("contact")}
              className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3.5 text-sm font-black text-white shadow-xl shadow-blue-700/25 transition hover:-translate-y-0.5 hover:bg-blue-700"
            >
              Request Demo <ArrowRight size={16} />
            </button>
            <button
              type="button"
              onClick={() => scrollTo("operations")}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-6 py-3.5 text-sm font-black text-slate-950 transition hover:border-slate-400 hover:bg-slate-50"
            >
              See the Flow
            </button>
          </div>

          <div className="mt-7 grid max-w-xl grid-cols-1 gap-4 sm:grid-cols-3">
            {[
              ["Clients", "Contacts and history"],
              ["Inventory", "Photos, rates, location"],
              ["Accounts", "Invoices and Tally data"],
            ].map(([label, value]) => (
              <div key={label} className="border-l-2 border-blue-600 pl-4">
                <div className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-600">{label}</div>
                <div className="mt-1 text-sm font-black text-slate-900">{value}</div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15, ease: "easeOut" }}
        >
          <ProductPanel />
        </motion.div>
      </div>
    </section>
  );
}

/* ----------------------------- why hoardexa ----------------------------- */

function WhyHoardexa() {
  return (
    <section id="why-hoardexa" className="scroll-mt-24 border-t border-slate-200 bg-white py-14 lg:py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          center
          eyebrow="Why Hoardexa"
          title="Built for the way outdoor advertising teams actually work."
        />

        <div className="mt-9 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {whyHoardexa.map((item, index) => (
            <motion.div
              key={item.title}
              {...reveal}
              transition={{ duration: 0.5, delay: index * 0.05, ease: "easeOut" }}
              className={`group relative overflow-hidden rounded-2xl border p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-xl ${
                item.featured
                  ? "border-blue-600 bg-blue-600 text-white shadow-blue-700/20 md:col-span-2 lg:col-span-3"
                  : "border-slate-200 bg-white hover:border-blue-300 hover:shadow-blue-900/5"
              }`}
            >
              <div
                className={`flex h-11 w-11 items-center justify-center rounded-xl border transition ${
                  item.featured
                    ? "border-white/25 bg-white/15 text-white"
                    : "border-blue-100 bg-blue-50 text-blue-700 group-hover:bg-blue-600 group-hover:text-white"
                }`}
              >
                <item.icon size={21} />
              </div>
              <h3 className={`mt-4 font-display text-lg font-black ${item.featured ? "text-white" : "text-slate-950"}`}>
                {item.title}
              </h3>
              <p
                className={`mt-2 text-sm font-semibold leading-relaxed ${
                  item.featured ? "max-w-4xl text-blue-50" : "text-slate-950"
                }`}
              >
                {item.text}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------- operations ------------------------------ */

function Operations() {
  const flowRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: flowRef,
    offset: ["start center", "end center"],
  });
  const flowProgress = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 28,
    restDelta: 0.001,
  });

  return (
    <section id="operations" className="scroll-mt-24 border-t border-slate-200 bg-white py-14 lg:py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div {...reveal} className="mb-8 text-center">
          <div className="flex items-center justify-center gap-3">
            <span className="h-px w-10 bg-blue-600" />
            <span className="text-[11px] font-black uppercase tracking-[0.24em] text-blue-600">Simple Flow</span>
            <span className="h-px w-10 bg-blue-600" />
          </div>
        </motion.div>

        <div
          ref={flowRef}
          className="relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-b from-white via-blue-50/35 to-white px-4 py-7 shadow-2xl shadow-slate-950/10 sm:px-6 lg:px-10 lg:py-10"
        >
          <div className="pointer-events-none absolute inset-0 opacity-[0.55]">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 24px 24px, rgb(148 163 184 / 0.28) 1px, transparent 1px)",
                backgroundSize: "48px 48px",
              }}
            />
          </div>
          <div className="pointer-events-none absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-transparent via-blue-600 to-transparent" />

          <div className="pointer-events-none absolute bottom-28 left-8 top-10 w-1.5 overflow-hidden rounded-full bg-slate-200/90 ring-4 ring-white/80 md:left-1/2 md:-translate-x-1/2">
            <motion.div
              className="h-full w-1.5 origin-top rounded-full bg-gradient-to-b from-blue-500 via-blue-600 to-blue-700 shadow-lg shadow-blue-600/40"
              style={{ scaleY: flowProgress }}
            />
          </div>

          <div className="relative grid gap-7">
            {steps.map((step, index) => (
              <motion.div
                key={step.num}
                {...reveal}
                transition={{ duration: 0.5, delay: index * 0.06, ease: "easeOut" }}
                className={`group relative ml-8 max-w-[540px] overflow-hidden rounded-2xl border border-slate-200 bg-white/95 p-5 shadow-lg shadow-slate-950/5 backdrop-blur transition hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-xl hover:shadow-blue-900/10 md:ml-0 md:w-[calc(50%-44px)] ${
                  index % 2 === 0 ? "md:justify-self-start" : "md:justify-self-end"
                }`}
              >
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 via-sky-400 to-emerald-400 opacity-80" />
                <div
                  className={`absolute top-8 h-px w-8 bg-blue-600 shadow-sm shadow-blue-600/30 md:w-10 ${
                    index % 2 === 0
                      ? "-left-8 md:left-auto md:-right-10"
                      : "-left-8 md:-left-10"
                  }`}
                />
                <div
                  className={`absolute top-[23px] flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-blue-600 shadow-lg shadow-blue-700/25 ${
                    index % 2 === 0
                      ? "-left-[42px] md:left-auto md:-right-[54px]"
                      : "-left-[42px] md:-left-[54px]"
                  }`}
                >
                  <span className="h-2 w-2 rounded-full bg-white" />
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-blue-100 bg-blue-50 text-blue-700 shadow-inner transition group-hover:bg-blue-600 group-hover:text-white">
                    <step.icon size={20} />
                  </div>
                  <div>
                    <h3 className="font-display text-base font-black text-slate-950">{step.title}</h3>
                    <p className="mt-1.5 text-sm font-medium leading-relaxed text-slate-950">{step.text}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div {...reveal} className="relative mt-8 flex justify-center">
            <button
              type="button"
              onClick={() => scrollTo("contact")}
              className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3.5 text-sm font-black text-white shadow-xl shadow-blue-700/25 transition hover:-translate-y-0.5 hover:bg-blue-700"
            >
              See it on your Data <ArrowRight size={16} />
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- pricing ------------------------------- */

function Pricing() {
  return (
    <section id="pricing" className="scroll-mt-24 border-t border-slate-200 bg-white py-14 lg:py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeader
          center
          eyebrow="Pricing"
          title="Plans that match how your business operates."
          text="Choose by how far you want to go: faster proposals, team work, custom PPT formats, finance reports, or full accounting exports."
        />

        <div className="mt-9 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              {...reveal}
              transition={{ duration: 0.5, delay: index * 0.06, ease: "easeOut" }}
              className={`relative flex flex-col rounded-2xl border p-6 transition hover:-translate-y-1 ${
                plan.highlighted
                  ? "border-blue-600 bg-blue-600 text-white shadow-2xl shadow-blue-700/25"
                  : "border-slate-200 bg-white shadow-sm hover:border-blue-300 hover:shadow-xl hover:shadow-blue-900/5"
              }`}
            >
              {plan.highlighted && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-slate-950 px-3.5 py-1.5 text-[10px] font-black uppercase tracking-[0.16em] text-white shadow-lg">
                  Recommended
                </div>
              )}

              <h3 className={`font-display text-xl font-black ${plan.highlighted ? "text-white" : "text-slate-950"}`}>
                {plan.name}
              </h3>
              <p
                className={`mt-2 min-h-[40px] text-sm font-semibold leading-relaxed ${
                  plan.highlighted ? "text-blue-100" : "text-slate-600"
                }`}
              >
                {plan.tagline}
              </p>

              <div className={`my-5 h-px ${plan.highlighted ? "bg-white/20" : "bg-slate-200"}`} />

              <ul className="flex-1 space-y-3">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2.5 text-sm font-semibold">
                    <Check
                      size={16}
                      className={`mt-0.5 shrink-0 ${plan.highlighted ? "text-blue-200" : "text-blue-600"}`}
                    />
                    <span className={plan.highlighted ? "text-white/95" : "text-slate-700"}>{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-7">
                <div
                  className={`mb-3 text-center text-[11px] font-black uppercase tracking-[0.14em] ${
                    plan.highlighted ? "text-blue-200" : "text-slate-500"
                  }`}
                >
                  Custom pricing
                </div>
                <button
                  type="button"
                  onClick={() => scrollTo("contact")}
                  className={`inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-black transition ${
                    plan.highlighted
                      ? "bg-white text-blue-700 hover:bg-blue-50"
                      : "border border-slate-300 bg-white text-slate-950 hover:border-blue-500 hover:text-blue-700"
                  }`}
                >
                  Contact Us <ArrowRight size={15} />
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.p {...reveal} className="mt-8 text-center text-sm font-semibold text-slate-500">
          Not sure which plan fits? <button type="button" onClick={() => scrollTo("contact")} className="font-black text-blue-600 underline-offset-4 hover:underline">Talk to us</button> — we will recommend one based on your inventory and team size.
        </motion.p>
      </div>
    </section>
  );
}

/* ------------------------------ testimonials ----------------------------- */

function Testimonials() {
  return (
    <section id="testimonials" className="scroll-mt-24 border-t border-slate-200 bg-white py-14 lg:py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          center
          eyebrow="Testimonials"
          title="Trusted by owners who run real inventory."
          text="Teams managing hoardings across Maharashtra use Hoardexa to close the gap between the field, the office, and the owner's desk."
        />

        <div className="mt-9 grid grid-cols-1 gap-5 md:grid-cols-3">
          {testimonials.map((item, index) => (
            <motion.figure
              key={item.name}
              {...reveal}
              transition={{ duration: 0.5, delay: index * 0.08, ease: "easeOut" }}
              className="flex flex-col rounded-2xl border border-slate-200 bg-white p-7 shadow-sm transition hover:-translate-y-1 hover:border-blue-300 hover:shadow-xl hover:shadow-blue-900/5"
            >
              <div className="flex items-center justify-between">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <Quote size={18} />
                </div>
                <div className="flex gap-0.5 text-amber-400">
                  {Array.from({ length: 5 }).map((_, starIndex) => (
                    <Star key={starIndex} size={14} fill="currentColor" />
                  ))}
                </div>
              </div>

              <blockquote className="mt-5 flex-1 text-sm font-semibold leading-relaxed text-slate-700">
                “{item.quote}”
              </blockquote>

              <figcaption className="mt-6 border-t border-slate-100 pt-5">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-950 font-display text-sm font-black text-white">
                    {item.name.split(" ").map((part) => part[0]).join("")}
                  </div>
                  <div>
                    <div className="font-display text-sm font-black text-slate-950">{item.name}</div>
                    <div className="text-xs font-bold text-slate-500">{item.role}</div>
                    <div className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-[0.12em] text-blue-600">
                      <MapPin size={11} /> {item.city}
                    </div>
                  </div>
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------- faq --------------------------------- */

function FAQ() {
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" className="scroll-mt-24 border-t border-slate-200 bg-white py-14 lg:py-16">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <SectionHeader center eyebrow="FAQ" title="Common questions" />

        <div className="mt-7 space-y-2.5">
          {faqs.map((item, index) => {
            const isOpen = open === index;
            return (
              <motion.div
                key={item.q}
                {...reveal}
                transition={{ duration: 0.4, delay: index * 0.04, ease: "easeOut" }}
                className={`overflow-hidden rounded-xl border bg-white transition ${
                  isOpen ? "border-slate-950 shadow-md shadow-slate-950/5" : "border-slate-300"
                }`}
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : index)}
                  className="flex w-full items-center justify-between gap-4 px-4 py-3.5 text-left sm:px-5"
                >
                  <span className="font-display text-sm font-semibold text-slate-950 sm:text-base">{item.q}</span>
                  <span
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border transition ${
                      isOpen
                        ? "rotate-180 border-slate-950 bg-blue-50 text-blue-600"
                        : "border-slate-200 text-slate-500"
                    }`}
                  >
                    <ChevronDown size={16} />
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="border-t border-slate-100 px-4 py-3.5 text-sm font-medium leading-relaxed text-slate-950 sm:px-5">
                        {item.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- contact ------------------------------- */

function Contact() {
  const formRef = useRef(null);
  const [status, setStatus] = useState("idle");
  const [errors, setErrors] = useState({});

  const inputClass =
    "h-11 w-full rounded-xl border border-slate-300 bg-white px-3.5 text-sm font-semibold text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10";

  async function handleSubmit(event) {
    event.preventDefault();
    const formData = Object.fromEntries(new FormData(formRef.current).entries());
    const nextErrors = {};
    if (!formData.from_name?.trim()) nextErrors.from_name = "Please enter your name";
    if (!formData.from_email?.trim()) nextErrors.from_email = "Please enter your email";
    if (!formData.message?.trim()) nextErrors.message = "Please enter a message";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setStatus("sending");
    try {
      await api.post("/contact", {
        name: formData.from_name,
        email: formData.from_email,
        company: formData.company || "",
        message: formData.message,
      });
      setStatus("success");
      formRef.current.reset();
    } catch {
      setStatus("error");
    }
  }

  return (
    <section id="contact" className="scroll-mt-24 border-t border-slate-200 bg-white py-14 lg:py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-3xl border border-slate-200 shadow-xl shadow-slate-950/5">
          <div className="grid lg:grid-cols-[0.9fr_1.1fr]">
            {/* left panel */}
            <div className="relative overflow-hidden bg-blue-600 p-8 text-white sm:p-10">
              <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
              <div className="pointer-events-none absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-blue-400/30 blur-2xl" />

              <div className="relative">
                <div className="text-[11px] font-black uppercase tracking-[0.24em] text-blue-200">Request a Demo</div>
                <h2 className="mt-3 font-display text-3xl font-black tracking-tight sm:text-4xl">
                  See your own sales-to-accounts flow.
                </h2>
                <p className="mt-4 max-w-md text-sm font-semibold leading-relaxed text-blue-100">
                  Tell us your cities, approximate hoarding count, and how you create proposals today. We will show the
                  demo around your real work and recommend the right plan.
                </p>

                <div className="mt-8 space-y-4 text-sm font-bold">
                  {[
                    "Client and hoarding setup walkthrough",
                    "Custom proposal PPT workflow",
                    "Campaign and billing handoff",
                    "Plan and pricing recommendation",
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/15">
                        <Check size={13} />
                      </span>
                      {item}
                    </div>
                  ))}
                </div>

                <div className="mt-10 flex items-center gap-2 text-xs font-black uppercase tracking-[0.14em] text-blue-200">
                  <ShieldCheck size={15} /> Your details stay private
                </div>

                <div className="mt-6 grid gap-3 border-t border-white/15 pt-6">
                  {contactEmails.map((item) => (
                    <a
                      key={item.email}
                      href={`mailto:${item.email}`}
                      className="group flex items-start gap-3 rounded-xl bg-white/10 p-3 transition hover:bg-white/15"
                    >
                      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/15 text-white">
                        <Mail size={15} />
                      </span>
                      <span>
                        <span className="block text-xs font-black uppercase tracking-[0.14em] text-blue-100">{item.label}</span>
                        <span className="mt-0.5 block text-sm font-black text-white group-hover:underline">{item.email}</span>
                        <span className="mt-0.5 block text-xs font-semibold text-blue-100">{item.note}</span>
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* form panel */}
            <form ref={formRef} onSubmit={handleSubmit} className="bg-white p-8 sm:p-10">
              {status === "success" ? (
                <div className="flex min-h-80 flex-col items-center justify-center text-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 text-emerald-600">
                    <CheckCircle2 size={30} />
                  </div>
                  <h3 className="mt-5 font-display text-xl font-black text-slate-950">Request sent</h3>
                  <p className="mt-2 max-w-sm text-sm font-medium leading-relaxed text-slate-600">
                    Your inquiry has been logged. The team will contact you within 24 hours with a demo slot and a
                    pricing recommendation.
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="mt-6 rounded-xl border border-slate-300 px-5 py-2.5 text-sm font-black text-slate-900 transition hover:bg-slate-50"
                  >
                    Send Another
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <label className="space-y-1.5">
                      <span className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-600">Name *</span>
                      <input name="from_name" className={inputClass} placeholder="Full name" />
                      {errors.from_name && <span className="text-xs font-bold text-red-600">{errors.from_name}</span>}
                    </label>
                    <label className="space-y-1.5">
                      <span className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-600">
                        Work Email *
                      </span>
                      <input name="from_email" type="email" className={inputClass} placeholder="you@company.com" />
                      {errors.from_email && (
                        <span className="text-xs font-bold text-red-600">{errors.from_email}</span>
                      )}
                    </label>
                  </div>
                  <label className="block space-y-1.5">
                    <span className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-600">Company</span>
                    <input name="company" className={inputClass} placeholder="Agency or owner group" />
                  </label>
                  <label className="block space-y-1.5">
                    <span className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-600">
                      Message *
                    </span>
                    <textarea
                      name="message"
                      rows={5}
                      className={`${inputClass} h-auto resize-none py-3`}
                      placeholder="Tell us your cities, approximate hoarding count, how you make proposals today, and what you want to improve first."
                    />
                    {errors.message && <span className="text-xs font-bold text-red-600">{errors.message}</span>}
                  </label>

                  {status === "error" && (
                    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs font-bold text-red-700">
                      Request failed. Please check your connection and try again.
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === "sending"}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3.5 text-sm font-black text-white shadow-lg shadow-blue-700/25 transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {status === "sending" ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Sending...
                      </>
                    ) : (
                      <>
                        <Send size={15} /> Send Inquiry
                      </>
                    )}
                  </button>

                  <div className="border-t border-slate-200 pt-5">
                    <div className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-500">
                      What happens next
                    </div>
                    <div className="mt-3 grid gap-3">
                      {[
                        "We review your cities, inventory size, and current proposal process.",
                        "You get a focused walkthrough using examples close to your real workflow.",
                        "We share the right plan and setup path for your team.",
                      ].map((item, index) => (
                        <div key={item} className="flex items-start gap-3 text-sm font-semibold leading-relaxed text-slate-700">
                          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-50 text-xs font-black text-blue-700">
                            {index + 1}
                          </span>
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- footer -------------------------------- */

function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer className="bg-slate-950 pt-16 text-slate-300">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 pb-12 md:grid-cols-[1.2fr_0.8fr_0.8fr_1fr]">
          <div>
            <BrandMark light />
            <p className="mt-5 max-w-sm text-sm font-medium leading-relaxed text-slate-400">
              A simple way for hoarding owners and outdoor advertising teams to manage clients, sites, proposals,
              campaigns, billing, and accounts in one place.
            </p>
            <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-emerald-500/25 bg-emerald-500/10 px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.14em] text-emerald-300">
              <ShieldCheck size={13} /> Secure by design
            </div>
          </div>

          <div>
            <div className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">Product</div>
            <div className="mt-4 grid gap-2.5 text-sm font-bold">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  type="button"
                  onClick={() => scrollTo(link.id)}
                  className="w-fit text-slate-300 transition hover:text-white"
                >
                  {link.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">Company</div>
            <div className="mt-4 grid gap-2.5 text-sm font-bold">
              <button
                type="button"
                onClick={() => scrollTo("contact")}
                className="w-fit text-slate-300 transition hover:text-white"
              >
                Request Demo
              </button>
              <Link to="/login" className="w-fit text-slate-300 transition hover:text-white">
                Sign In
              </Link>
              <Link to="/privacy-policy" className="w-fit text-slate-300 transition hover:text-white">
                Privacy Policy
              </Link>
              <Link to="/careers" className="w-fit text-slate-300 transition hover:text-white">
                Careers
              </Link>
            </div>
          </div>

          <div>
            <div className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">Contact</div>
            <div className="mt-4 grid gap-2.5 text-sm font-bold">
              {[...contactEmails, { label: "Careers", email: "careers@hoardexa.com" }].map((item) => (
                <a key={item.email} href={`mailto:${item.email}`} className="w-fit text-slate-300 transition hover:text-white">
                  {item.email}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-3 border-t border-white/10 py-6 text-xs font-bold text-slate-500 sm:flex-row">
          <div>© {year} SkillEdge Technologies. All rights reserved.</div>
          <div>Made for outdoor media operators.</div>
        </div>
      </div>
    </footer>
  );
}

/* ---------------------------------- page --------------------------------- */

export function Home() {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen bg-white text-slate-950">
      <Navbar />
      <Hero />
      <WhyHoardexa />
      <Operations />
      <Pricing />
      <FAQ />
      <Contact />
      <Footer />
    </motion.div>
  );
}
